/**
 * Géolocalisation, derrière une interface.
 *
 * L'implémentation renvoie une raison d'échec explicite plutôt que de lever
 * une exception : c'est l'interface qui décide du message, et chaque raison
 * mérite une phrase différente. En particulier, un refus du navigateur pour
 * cause de contexte non sécurisé n'est pas une panne mais une règle de
 * sécurité, et le dire évite à l'utilisateur de croire que l'app est cassée.
 */
export type LocateFailure =
  /** Le navigateur ne propose pas l'API. */
  | 'unsupported'
  /** Page servie en http sur autre chose que localhost. */
  | 'insecure'
  /** L'utilisateur a refusé. */
  | 'denied'
  /** Position indisponible, ou délai dépassé. */
  | 'unavailable';

export type LocateResult =
  | { ok: true; lat: number; lon: number }
  | { ok: false; reason: LocateFailure };

export interface Geolocator {
  locate(): Promise<LocateResult>;
}

const TIMEOUT_MS = 10_000;
const MAX_AGE_MS = 600_000;

export function createWebGeolocator(): Geolocator {
  return {
    locate() {
      if (!navigator.geolocation) {
        return Promise.resolve({ ok: false, reason: 'unsupported' } as const);
      }
      /*
       * La localisation n'est permise qu'en HTTPS ou sur localhost. Depuis un
       * téléphone qui consulte une adresse IP en http, elle est bloquée par
       * le navigateur. On le détecte avant d'appeler l'API, pour pouvoir
       * l'expliquer au lieu d'afficher un échec incompréhensible.
       */
      if (!window.isSecureContext) {
        return Promise.resolve({ ok: false, reason: 'insecure' } as const);
      }

      return new Promise<LocateResult>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) =>
            resolve({
              ok: true,
              lat: position.coords.latitude,
              lon: position.coords.longitude,
            }),
          (error) =>
            resolve({
              ok: false,
              reason: error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable',
            }),
          { timeout: TIMEOUT_MS, maximumAge: MAX_AGE_MS },
        );
      });
    },
  };
}
